/**
 * BillingDetailsCard — the client's bill-to on the Pre-billing tab.
 *
 * Shows the PRIMARY billing contact (the person who receives the
 * invoices — often a guardian or attorney, distinct from the client):
 * name, relationship, email, address. No primary on file is the state
 * every Kith-native client starts in; the empty state speaks to its
 * reader: a seat that can set the bill-to reads that no one is set yet,
 * beside the control that sets it, and any other seat reads that the
 * office sets who receives the bill.
 *
 * Capability: canSetBillTo (the office seats) gets Add/Edit -> the
 * in-file dialog. Refresh from care contact needs set_bill_to and
 * manage_client_identity. Every seat that sees the card (the
 * view_invoice holders, the tab's gate) reads it.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { Dialog } from '@/components/primitives/Dialog';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { TextInput } from '@/components/primitives/TextInput';
import { FLD, FLD_HELP, FLD_LABEL } from '@/components/primitives/modalFormStyles';
import { useTenant } from '@/lib/auth';
import { canManageClientIdentity, canSetBillTo } from '@/lib/capabilities';
import { ApiError } from '@/lib/api';
import { contactSentence, saveFailureText, type SupportContact } from '@/lib/errorCopy';
import { fieldSentence } from '@/lib/fieldErrorCopy';
import { focusFirstGap } from '@/lib/formGaps';
import { useSupportContact } from '@/hooks/useSupportContact';
import { BillingHouseholdLine } from '@/components/clientRecord/BillingHouseholdLine';
import {
  AddressCheckChoices,
  AddressCheckPrompt,
  AddressFields,
  useAddressForm,
  type AddressFormParts,
} from '@/components/clientRecord/AddressesCard';
import {
  billingAddressPartsOf,
  billingContactsKey,
  contactEmailProblem,
  primaryContactOf,
  putBillingContact,
  refreshBillingContact,
  useBillingContacts,
  type BillingContactWire,
} from '@/lib/clientBillingContacts';

/** The dialog's fact-shaped save errors, plain words. */
function saveErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Billing details are set by the office. Ask the office to change them.';
    if (err.status === 400) return "That didn't save. Enter the contact's name and their full email address, with the @.";
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact)
  );
}

interface BillingContactDialogProps {
  clientId: string;
  /** null = add the first bill-to; a row = edit it in place. */
  existing: BillingContactWire | null;
  onSaved: (contacts: BillingContactWire[]) => void;
  onClose: () => void;
}

/** The sentences on screen, one per box. A press sets one for every
 *  gap; leaving the Email box sets the email's; each clears on the next
 *  change to its own box. */
interface ShownSentences {
  name?: string;
  email?: string;
}

const NAME_ID = 'billing-contact-name';
const EMAIL_ID = 'billing-contact-email';
/** The mailing address's fields' id prefix (billing-contact-address-*). */
const ADDRESS_PREFIX = 'billing-contact';

/** Whether typed parts equal the stored ones, part by part. */
function samePartsAs(a: AddressFormParts, b: AddressFormParts): boolean {
  return a.line1 === b.line1 && a.line2 === b.line2 && a.city === b.city && a.state === b.state && a.zip === b.zip;
}

/** The one line under the address on filling the form from People. */
const PEOPLE_LINE =
  'To use someone from People, tick Billing contact on their contact; their details fill this in.';

/**
 * The bill-to's form: Name and Relationship on one row from `sm`, then
 * Email, the mailing address (the shared address form, a street with
 * any part), and one line on filling this from a care contact instead of
 * retyping it. The email box is never capitalised; leaving it after
 * typing trims and lower-cases the value and shows its sentence when the
 * shape is wrong. Save stays pressable: pressed with a gap it sends
 * nothing, shows each gap's sentence in its box's own slot and moves
 * focus to the first gap; a changed street is checked before the save;
 * an edit with nothing changed closes without sending. A bill-to held as
 * text only opens on empty fields under "On file as", and its text is
 * kept while they stay empty.
 */
function BillingContactDialog({
  clientId,
  existing,
  onSaved,
  onClose,
}: BillingContactDialogProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [name, setName] = useState(existing?.contact_name ?? '');
  const [relationship, setRelationship] = useState(existing?.relationship ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState<ShownSentences>({});
  // Whether the Email box has been typed in: an untouched stored value
  // is left as it is when the box is merely tabbed through.
  const emailTyped = useRef(false);
  const contact = useSupportContact();

  /** The three values as they travel: trimmed, the email lower-cased,
   *  empty as null. */
  const wire = {
    contact_name: name.trim(),
    relationship: relationship.trim() === '' ? null : relationship.trim(),
    email: email.trim() === '' ? null : email.trim().toLowerCase(),
  };
  const storedParts = existing === null ? null : billingAddressPartsOf(existing);
  // A stored address with no parts: shown as "On file as" over the
  // fields, which open on the parts the text suggests (empty when the
  // split would keep it), and kept while they stay empty.
  const textOnly = existing !== null && storedParts === null && (existing.address ?? '').trim() !== '';

  // The request is built where Save is pressed and travels as the
  // mutation's variables: React Query hands a mutation its options in an
  // effect, so a mutationFn reading this render's values could send an
  // earlier render's.
  const saveMut = useMutation<BillingContactWire[], unknown, Parameters<typeof putBillingContact>[2]>({
    mutationFn: (body) => putBillingContact(tenantSlug, clientId, body),
    onSuccess: (contacts) => {
      onSaved(contacts);
      onClose();
    },
    onError: (err) => setError(saveErrorMessage(err, contact)),
  });
  const addressForm = useAddressForm({
    existing: storedParts,
    existingText: textOnly ? existing.address : null,
    rule: 'street',
    idPrefix: ADDRESS_PREFIX,
    done: false,
    saving: saveMut.isPending,
    onSave: (parts) => send(parts),
  });

  /** The gaps in drawn order: an empty Name, then an email whose shape is
   *  wrong (refused here because that address receives invoices). With a
   *  gap, every gap's sentence shows and focus moves to the first; nothing
   *  is sent. Then the address: empty fields send none (or, for a
   *  text-only bill-to, keep its text); typed fields go through the
   *  address form, which names its own gaps and checks a changed street
   *  before it hands the parts to send. */
  function save(): void {
    const gaps: ShownSentences = {};
    if (wire.contact_name === '') gaps.name = fieldSentence('Name', { kind: 'text' }, name);
    const emailSentence = contactEmailProblem(email);
    if (emailSentence !== null) gaps.email = emailSentence;
    const gapIds = [
      ...(gaps.name !== undefined ? [NAME_ID] : []),
      ...(gaps.email !== undefined ? [EMAIL_ID] : []),
    ];
    if (gapIds.length > 0) {
      setShown(gaps);
      focusFirstGap(gapIds);
      return;
    }
    if (addressForm.isEmpty()) {
      send(textOnly ? undefined : null);
      return;
    }
    addressForm.pressSave();
  }

  /** The PUT, with the address as parts, null for none, or undefined to
   *  keep the stored one. An edit whose values equal the stored ones
   *  closes without sending. */
  function send(address: AddressFormParts | null | undefined): void {
    const addressSame =
      address === undefined
        ? true
        : address === null
          ? existing !== null && (existing.address ?? null) === null
          : storedParts !== null && samePartsAs(storedParts, address);
    if (
      existing !== null &&
      wire.contact_name === existing.contact_name &&
      wire.relationship === existing.relationship &&
      wire.email === existing.email &&
      addressSame
    ) {
      onClose();
      return;
    }
    saveMut.mutate({
      ...(existing !== null ? { id: existing.id } : {}),
      ...wire,
      ...(address !== undefined ? { address } : {}),
      // This card manages THE bill-to; the save always lands as the
      // primary (the server clears any previous primary in the same
      // transaction).
      is_primary: true,
    });
  }
  /** Any change clears the save error and an open address prompt. */
  const clearError = (): void => {
    setError(null);
    addressForm.touched();
  };

  return (
    <Dialog
      title={existing !== null ? 'Edit billing details' : 'Add billing details'}
      size="md"
      testId="billing-contact-dialog"
      onClose={onClose}
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              addressForm.cancel();
              onClose();
            }}
          >
            Cancel
          </Button>
          {addressForm.prompting ? (
            <AddressCheckChoices
              phase={addressForm.check.phase}
              busy={saveMut.isPending}
              onKeep={() => addressForm.choose('checked', false)}
              onUse={() => addressForm.choose('checked', true)}
              onSaveAsTyped={() => addressForm.choose('saved_as_typed', false)}
            />
          ) : (
            <Button
              variant="primary"
              size="sm"
              disabled={saveMut.isPending || addressForm.checking}
              loading={saveMut.isPending || addressForm.checking}
              data-testid="billing-contact-save"
              onClick={save}
            >
              Save billing details
            </Button>
          )}
        </>
      }
    >
      {/* Two columns from `sm`, one below: Name and Relationship share a
          row; the email, the address, the People line and the save error
          span both. */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 py-2 sm:grid-cols-2">
        <Field
          label="Name"
          required
          htmlFor={NAME_ID}
          error={shown.name}
          errorTestId="billing-contact-name-problem"
        >
          <TextInput
            id={NAME_ID}
            data-testid="billing-contact-name-input"
            type="text"
            nameCase="person"
            className={FLD}
            value={name}
            onChange={(e) => {
              clearError();
              setShown((prev) => ({ ...prev, name: undefined }));
              setName(e.target.value);
            }}
            {...fieldBoxAria(NAME_ID, { required: true, error: shown.name !== undefined })}
          />
        </Field>
        <Field
          label="Relationship"
          htmlFor="billing-contact-relationship"
          help="For example Daughter, Attorney, Guardian."
        >
          <TextInput
            id="billing-contact-relationship"
            data-testid="billing-contact-relationship-input"
            type="text"
            nameCase="relationship"
            className={FLD}
            value={relationship}
            onChange={(e) => {
              clearError();
              setRelationship(e.target.value);
            }}
            {...fieldBoxAria('billing-contact-relationship', { help: true })}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field
            label="Email"
            htmlFor={EMAIL_ID}
            error={shown.email}
            errorTestId="billing-contact-email-problem"
          >
            <TextInput
              id={EMAIL_ID}
              data-testid="billing-contact-email-input"
              type="email"
              spellCheck={false}
              autoCapitalize="none"
              autoComplete="off"
              className={FLD}
              value={email}
              onChange={(e) => {
                clearError();
                emailTyped.current = true;
                setShown((prev) => ({ ...prev, email: undefined }));
                setEmail(e.target.value);
              }}
              onBlur={(e) => {
                if (!emailTyped.current) return;
                const lowered = e.currentTarget.value.trim().toLowerCase();
                setEmail(lowered);
                setShown((prev) => ({ ...prev, email: contactEmailProblem(lowered) ?? undefined }));
              }}
              {...fieldBoxAria(EMAIL_ID, { error: shown.email !== undefined })}
            />
          </Field>
        </div>
        <fieldset className="flex min-w-0 flex-col gap-2 sm:col-span-2">
          <legend className={FLD_LABEL}>Mailing address</legend>
          {textOnly && (
            <p data-testid="address-on-file-text" className="text-xs text-sage-800">
              On file as: {existing?.address}
            </p>
          )}
          <AddressFields idPrefix={ADDRESS_PREFIX} {...addressForm.fields} />
          <AddressCheckPrompt phase={addressForm.check.phase} />
        </fieldset>
        <p data-testid="billing-contact-people-line" className={`${FLD_HELP} sm:col-span-2`}>
          {PEOPLE_LINE}
        </p>
        {error !== null && (
          <p role="alert" data-testid="billing-contact-error" className="text-xs text-pepper-deep sm:col-span-2">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

export interface BillingDetailsCardProps {
  clientId: string;
  /** For the explainer's first name (already on the page). */
  clientName: string;
}

export function BillingDetailsCard({ clientId, clientName }: BillingDetailsCardProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();
  const { whoami } = useTenant();
  const canManage = canSetBillTo(whoami);
  // Refresh re-projects the bill-to from the (encrypted) care contact: it
  // re-sets who receives the bill (set_bill_to) and it is a client-identity
  // WRITE that decrypts the contact (manage_client_identity). A seat
  // without both sees the nudge but not the button.
  const canRefresh = canSetBillTo(whoami) && canManageClientIdentity(whoami);
  const { contacts, isLoading, isError, error, refetch } = useBillingContacts(
    tenantSlug,
    clientId,
  );
  const [editing, setEditing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  // The error copy's contact, from the shell's cached tenant config: the
  // card makes no call of its own for it.
  const contact = useSupportContact({ enabled: false });

  const refreshMut = useMutation<BillingContactWire[], unknown, string>({
    mutationFn: (id) => refreshBillingContact(tenantSlug, clientId, id),
    onSuccess: (next) => {
      queryClient.setQueryData(billingContactsKey(tenantSlug, clientId), next);
      setRefreshError(null);
    },
    onError: () =>
      setRefreshError(
        contactSentence(
          "That didn't copy from the care contact. Try again, and if it happens again, tell {contact_system}.",
          contact,
        ),
      ),
  });

  const firstName = clientName.trim().split(/\s+/)[0] ?? 'this client';
  const primary = primaryContactOf(contacts);

  return (
    <div
      className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-6"
      data-testid="billing-details-card"
    >
      <h3 className="font-display text-lg text-sage-800 mb-1">Billing details</h3>
      <p className="text-sm text-ink-soft mb-3" data-testid="billing-details-explainer">
        Who receives {firstName}&rsquo;s invoices.
      </p>

      <AsyncBoundary isLoading={isLoading} isError={isError} error={error} onRetry={refetch}>
        {contacts !== null && (
          <div className="flex flex-wrap items-start justify-between gap-3">
            {primary !== null ? (
              <div className="text-sm" data-testid="billing-details-primary">
                <div className="font-medium text-ink" data-testid="billing-contact-name">
                  {primary.contact_name}
                  {primary.relationship !== null && (
                    <span className="font-normal text-ink-soft"> ({primary.relationship})</span>
                  )}
                </div>
                {primary.email !== null && (
                  <div className="text-ink-soft" data-testid="billing-contact-email">
                    {primary.email}
                  </div>
                )}
                {primary.address !== null && (
                  <div
                    className="text-ink-soft whitespace-pre-line"
                    data-testid="billing-contact-address"
                  >
                    {primary.address}
                  </div>
                )}
                {primary.source_contact_id !== null && (
                  <div className="mt-1.5 space-y-1.5" data-testid="billing-details-provenance">
                    {/* Generic — never the contact's NAME (encrypted / RLS-gated;
                        the projected name already renders above). */}
                    <p className="text-xs text-ink-soft">Filled from a care contact.</p>
                    {primary.source_stale && (
                      <div
                        className="flex flex-wrap items-center gap-2"
                        data-testid="billing-details-stale"
                      >
                        <Chip variant="caution" data-testid="billing-details-stale-chip">
                          Care contact changed
                        </Chip>
                        {canRefresh ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="billing-details-refresh"
                            loading={refreshMut.isPending}
                            disabled={refreshMut.isPending}
                            onClick={() => refreshMut.mutate(primary.id)}
                          >
                            Refresh from care contact
                          </Button>
                        ) : (
                          <span
                            className="text-xs text-ink-soft"
                            data-testid="billing-details-refresh-disabled"
                          >
                            The office can refresh this.
                          </span>
                        )}
                      </div>
                    )}
                    {refreshError !== null && (
                      <p
                        role="alert"
                        className="text-xs text-pepper"
                        data-testid="billing-details-refresh-error"
                      >
                        {refreshError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : canManage ? (
              <div
                className="text-sm font-medium text-pepper"
                data-testid="billing-details-missing"
              >
                Add billing details to say who receives the bill.
              </div>
            ) : (
              <div className="text-sm text-ink-soft" data-testid="billing-details-missing">
                The office sets who receives the bill.
              </div>
            )}
            {canManage && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="billing-details-edit"
                onClick={() => setEditing(true)}
              >
                {primary !== null ? 'Edit' : 'Add billing details'}
              </Button>
            )}
          </div>
        )}
      </AsyncBoundary>

      {/* A couple on one invoice (0215): whom this client bills with. */}
      <BillingHouseholdLine tenantSlug={tenantSlug} clientId={clientId} firstName={firstName} />

      {editing && (
        <BillingContactDialog
          clientId={clientId}
          existing={primary}
          onSaved={(next) =>
            queryClient.setQueryData(billingContactsKey(tenantSlug, clientId), next)
          }
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
